<template>
  <div
    class="am3-stepper flex items-center justify-start rounded-md w-full"
    :class="hasDivider ? 'overflow-hidden' : ''"
    :style="containerStyles"
  >
    <template v-if="hasDivider">
      <div
        class="steps-progress bg-surface-strong transition-all duration-500 h-full"
        :style="{ width: `${(currentStep / stepsCount) * 100}%` }"
      />
    </template>
    <template v-else>
      <div
        v-for="step in stepsCount"
        :key="`step-${step}`"
        class="step flex flex-col items-center justify-center transition-all duration-300"
        :class="[step <= currentStep ? 'completed' : 'incomplete', step === currentStep ? 'current' : '']"
      >
        <div class="step-indicator flex items-center justify-center rounded-full transition-colors duration-300">
          <span
            v-if="step < currentStep"
            class="text-white"
          >
            <AM3Icon
              type="check"
              size="sm"
              color="white"
            />
          </span>
          <span
            v-else
            class="text-sm font-semibold"
          >{{ step }}</span>
        </div>
        <div
          v-if="step < stepsCount"
          class="step-connector"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import type { PropType } from 'vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'

/**
 * AM3Stepper component
 *
 * A component for displaying multi step processes
 */
export default defineComponent({
  name: 'AM3Stepper',

  components: {
    AM3Icon,
  },

  props: {
    // Current active step (1-based index)
    currentStep: {
      type: Number,
      default: 1,
      validator: (value: number) => value >= 1,
    },

    // Total number of steps
    stepsCount: {
      type: Number,
      required: true,
      validator: (value: number) => value >= 1,
    },

    // Use a divider style instead of numbered steps (simpler progress bar)
    hasDivider: {
      type: Boolean,
      default: false,
    },

    // Additional container styles
    containerStyles: {
      type: Object as any,
      default: () => ({}),
    },

    // Data test id
    dataTestId: {
      type: String,
      default: 'stepper',
    },
  },

  setup(props) {
    return {
      generateDataTestId,
    }
  },
})
</script>

<style scoped>
.am3-stepper {
  height: 5px;
  background-color: var(--surface-strong);
}

/* For divider style */
.steps-progress {
  background-color: var(--surface-brand);
  border-radius: inherit;
}

/* For step style (not used when hasDivider is true) */
.step {
  flex: 1;
  position: relative;
}

.step-indicator {
  width: 24px;
  height: 24px;
  background-color: var(--surface-light);
  border: 2px solid var(--border-color-weak);
  color: var(--font-colour-weak);
}

.step.completed .step-indicator {
  background-color: var(--surface-brand);
  border-color: var(--surface-brand);
  color: var(--font-colour-white);
}

.step.current .step-indicator {
  border-color: var(--surface-brand);
  color: var(--font-colour-brand);
}

.step-connector {
  position: absolute;
  top: 12px;
  left: 50%;
  width: 100%;
  height: 2px;
  background-color: var(--border-color-weak);
}

.step.completed .step-connector {
  background-color: var(--surface-brand);
}
</style>
